import React from 'react';

import type {
  Component,
  FactComponent,
  GalleryComponent,
  GradeComponent,
  ImageComponent,
  SubheadingComponent,
  TextComponent,
  UrlComponent,
  VideoAssetComponent,
  AdComponent,
  IntegrationEnrichmentComponent,
  InternalLinkComponent,
} from '../../../../../core/data-sources/content-components/types.js';

import TextWrapper from './Text/TextWrapper.js';
import Image from './Image.js';
import Gallery from './Gallery/Gallery.js';
import FactBox from './FactBox.js';
import InlineGrade from './InlineGrade.js';
import Subheading from './Subheading.js';
import UrlWrapper from './UrlWrapper.js';
import VideoAssetPlayer from './VideoAssetPlayer.js';
import LinkInternal from './LinkInternal.js';
import Ad from '../../Ad/Ad.js';
import Integration from './Integration.js';

interface ComponentSelectorProps {
  item: Component;
  index: number;
  articleTitle: string;
  isMobile: boolean;
  groupIndex: number;
  isGuide: boolean;
}

type ComponentType =
  | 'text'
  | 'url'
  | 'image'
  | 'teaser'
  | 'subheading'
  | 'gallery'
  | 'fact'
  | 'grade'
  | 'video'
  | 'ad'
  | 'integration-enrichment'
  | 'default';

type ComponentMappingInterface = {
  [K in ComponentType]: (props: ComponentSelectorProps) => JSX.Element | null;
};

const isComponentType = <T extends Component>(
  component: Component,
  type: string,
): component is T => 'type' in component && component.type === type;

// TODO: clean up prop passing to components

const ComponentMapping: ComponentMappingInterface = {
  text: (props) =>
    isComponentType<TextComponent>(props.item, 'text') ? (
      <TextWrapper
        item={props.item}
        groupIndex={props.groupIndex}
        index={props.index}
      />
    ) : null,
  url: (props) =>
    isComponentType<UrlComponent>(props.item, 'url') ? (
      <UrlWrapper
        item={props.item}
        groupIndex={props.groupIndex}
        index={props.index}
        isMobile={props.isMobile}
      />
    ) : null,
  image: (props) =>
    isComponentType<ImageComponent>(props.item, 'image') ? (
      <Image
        {...props.item}
        isMobile={props.isMobile}
        articleTitle={props.articleTitle}
        lazyLoad
      />
    ) : null,
  teaser: (props) =>
    isComponentType<InternalLinkComponent>(props.item, 'teaser') ? (
      <LinkInternal {...props.item} />
    ) : null,
  subheading: (props) =>
    isComponentType<SubheadingComponent>(props.item, 'subheading') ? (
      <Subheading item={props.item} />
    ) : null,
  gallery: (props) =>
    isComponentType<GalleryComponent>(props.item, 'gallery') ? (
      <Gallery
        item={props.item}
        groupIndex={props.groupIndex}
        isMobile={props.isMobile}
        articleTitle={props.articleTitle}
      />
    ) : null,
  fact: (props) =>
    isComponentType<FactComponent>(props.item, 'fact') ? (
      <FactBox {...props.item} />
    ) : null,
  grade: ({ item, groupIndex, isGuide }) =>
    (groupIndex || isGuide) &&
    isComponentType<GradeComponent>(item, 'grade') ? (
      <InlineGrade item={item} groupIndex={groupIndex} isGuide={isGuide} />
    ) : null,
  video: (props) =>
    isComponentType<VideoAssetComponent>(props.item, 'video') ? (
      <VideoAssetPlayer video={props.item} isMobile={props.isMobile} />
    ) : null,
  ad: (props) =>
    isComponentType<AdComponent>(props.item, 'ad') ? (
      <Ad
        id={props.item.id}
        cssClass={props.item.cssClass}
        isMobile={props.isMobile}
      />
    ) : null,
  'integration-enrichment': (props) =>
    isComponentType<IntegrationEnrichmentComponent>(
      props.item,
      'integration-enrichment',
    ) ? (
      <Integration {...props.item} />
    ) : null,
  default: () => null,
};

const ComponentSelector: React.FC<ComponentSelectorProps> = ({
  item,
  index,
  articleTitle,
  groupIndex,
  isGuide,
  isMobile = false,
}) => {
  const commonProps = {
    item,
    index,
    articleTitle,
    groupIndex,
    isGuide,
    isMobile,
  };
  const Component =
    ComponentMapping[item.type as ComponentType] || ComponentMapping['default'];

  return <Component {...commonProps} />;
};

export default ComponentSelector;
